import { FormFieldsData, FormShowTemplate } from 'framework/form';
import {
  useNewProjectService,
  useProjectFinanceService,
  WASTE_STATUS,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { useParams } from 'react-router-dom';
import ChangeRequestActionButtons from 'domain/projects/newProject/finance/changeRequests/createupdate/ChangeRequestActionButtons';
import { ShowSection } from 'framework/ui/layout';
import { useWasteService, WASTE_REPORTING_METHOD, WASTE_TYPE } from 'domain/shared/waste';
import { useShowContext } from 'framework/show';
import {
  getRedmineIdentifierFromProject,
  ProjectDto,
  useProjectBootstrap,
  useRedmineIdentifier,
} from './WasteFormShared';
import WasteForm from '../form/WasteForm';

const CreateWasteLayout = () => {
  const { getById } = useNewProjectService();
  const { wasteSearch, createWaste, getWasteValidationSchema } = useProjectFinanceService();
  const { id } = useParams<{ id: string }>();
  const { data } = useShowContext();
  const { getFinanceUrl } = useNewProjectUrl();
  const { getProjects } = useWasteService();

  const { project } = useProjectBootstrap({
    id,
    contextProject: data?.project as ProjectDto | undefined,
    fetchProjectById: getById as (id: string) => Promise<ProjectDto>,
  });
  const redmineIdentifierFromContext = useRedmineIdentifier(project);

  const submitFormFunc = async (values: FormFieldsData) => {
    const projectData = data?.project || (await getById(id!));
    const redmineIdentifier = redmineIdentifierFromContext ?? getRedmineIdentifierFromProject(projectData);

    const basePayload = {
      type: values.type,
      alias: null,
      reason: values.reason,
      status: values.isDraft ? WASTE_STATUS.DRAFT : WASTE_STATUS.REGISTERED,
      davinciProjectId: id,
      budgetId: projectData?.defaultBudgetId || null,
    };

    if (values.type === WASTE_TYPE.OTHER_EXPENSES_WASTE) {
      return createWaste({
        ...basePayload,
        laborWasteDetails: null,
        otherWasteDetails: {
          cost: {
            amount: values.otherWasteDetails.cost?.amount,
            currency: values.otherWasteDetails.cost?.currency,
          },
          issueDate: values.otherWasteDetails.issueDate,
        },
      });
    }

    if (values.type === WASTE_TYPE.LABOR_COST_WASTE) {
      const { laborWasteDetails: details } = values;

      const reportingMethodHandlers: Record<string, () => any | Promise<any>> = {
        [WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS]: () => ({
          reportingMethod: details.reportingMethod,
          employeeAlias: details.employeeAlias || null,
          ticketsIds: details.ticketsIds || [],
          projectId: null,
          period: null,
        }),

        [WASTE_REPORTING_METHOD.BY_PROJECT_PERIOD]: async () => {
          const employeeAlias = details.employeeAlias || null;
          const period = details.period
            ? {
                startDate: details.period.startDate || '',
                endDate: details.period.endDate || '',
              }
            : null;

          const projects = await getProjects({ id: employeeAlias, period }).then((response: any) =>
            (response?.result || []).filter(
              (p: any) => (p.projectIdentifier ?? '').trim().toLowerCase() === redmineIdentifier.trim().toLowerCase(),
            ),
          );

          if (projects.length === 0) throw new Error(`No project matched Redmine identifier "${redmineIdentifier}".`);
          if (projects.length > 1)
            throw new Error(`Multiple projects matched Redmine identifier "${redmineIdentifier}".`);

          return {
            reportingMethod: details.reportingMethod,
            employeeAlias,
            ticketsIds: null,
            projectId: projects[0].id,
            period,
          };
        },

        [WASTE_REPORTING_METHOD.BY_TICKET_IDS]: () => ({
          reportingMethod: details.reportingMethod,
          employeeAlias: null,
          ticketsIds: details.ticketsIds || [],
          redmineProjectIdentifier: redmineIdentifier || null,
          projectId: null,
          period: null,
        }),
      };

      const handler = reportingMethodHandlers[details.reportingMethod];
      if (!handler) throw new Error(`Unknown reporting method: ${details.reportingMethod}`);

      const laborWasteDetails = await handler();
      return createWaste({
        ...basePayload,
        laborWasteDetails,
        otherWasteDetails: null,
      });
    }

    throw new Error(`Unknown waste type: ${values.type}`);
  };
  return (
    <FormShowTemplate
      getDataFunc={() =>
        wasteSearch({
          pageSize: 30,
          pageNumber: 0,
          filter: {
            davinciProjectId: {
              eq: id,
            },
          },
          sort: {
            createdAt: 'DESC',
          },
        })
      }
      submitFormFunc={submitFormFunc}
      getValidationSchemaFunc={getWasteValidationSchema}
      afterSubmit={{
        redirect: getFinanceUrl(id),
        message: 'You are awesome! The Waste has been successfully reported.',
      }}
    >
      <ShowSection title="Create: Waste" titleVariant="h2" sx={{ p: 2 }}>
        <WasteForm title="" isProjectVisible={false} redmineIdentifier={redmineIdentifierFromContext} />
        <ChangeRequestActionButtons />
      </ShowSection>
    </FormShowTemplate>
  );
};
export default CreateWasteLayout;
